// ============================================================
// View: Übersicht — KPIs, Finanzchart, Verteilungen, Datenqualität
// ============================================================

import { useMemo } from 'react';
import { Card, CardTitle, NetCell, EurCell } from '../components/ui';
import { KpiCard } from '../components/KpiCard';
import { FinanceChart, HBarChart, Legend } from '../components/charts';
import { artColor, CO } from '../theme';
import { useApp } from '../store';
import { inRange, kpi, monthlyRows, monthSpan, period, prevPeriod, artRows } from '../aggregator';
import { dstr, eur, fmtNum, pct, sEur, tstr } from '../format';
import { Icon } from '../icons';
import type { MonthRow } from '../types';

export function Uebersicht() {
  const { history, periodMode, customFrom, customTo, compare, setDetailId } = useApp();
  const work = history?.eintraege ?? [];
  const p = useMemo(() => period(periodMode, work, customFrom, customTo), [periodMode, work, customFrom, customTo]);
  const p2 = useMemo(() => prevPeriod(p), [p]);

  const filtered = useMemo(() => (p.full ? work.slice() : work.filter((e) => inRange(e, p))), [work, p]);
  const prevFiltered = useMemo(() => {
    if (!p2) return null;
    return work.filter((e) => inRange(e, p2));
  }, [work, p2]);

  const k = useMemo(() => kpi(filtered), [filtered]);
  const pk = prevFiltered ? kpi(prevFiltered) : null;

  const span = monthSpan(p);
  const perMonth = span > 0 ? k.einsatz / span : null;
  const nettoPerMonth = span > 0 ? k.netto / span : null;
  const prevSpan = p2 ? monthSpan(p2) : 0;
  const prevPerMonth = pk && prevSpan > 0 ? pk.einsatz / prevSpan : undefined;

  const months: MonthRow[] = useMemo(() => monthlyRows(filtered), [filtered]);
  const prevMonths: MonthRow[] | null = useMemo(() => {
    if (!compare || !prevFiltered) return null;
    return monthlyRows(prevFiltered);
  }, [compare, prevFiltered]);

  const arts = useMemo(() => artRows(filtered), [filtered]);

  const top = useMemo(
    () => filtered.filter((e) => e.gewinn > 0).sort((a, b) => b.gewinn - a.gewinn).slice(0, 5),
    [filtered],
  );

  const recent = useMemo(() => filtered.slice().sort((a, b) => (a.iso < b.iso ? 1 : -1)).slice(0, 6), [filtered]);

  // Datenqualitäts-Check: berechnete vs. importierte Summen
  const allK = useMemo(() => kpi(work), [work]);
  const summary = history?.zusammenfassung;
  const okE = !!summary && Math.abs(allK.einsatz - (summary.einsatzGesamt || 0)) < 0.005;
  const okG = !!summary && Math.abs(allK.gewinn - (summary.gewinnGesamt || 0)) < 0.005;
  const okAll = okE && okG;
  const calcNetto = allK.gewinn - allK.einsatz;
  const saldo = summary?.saldo ?? 0;
  const okSaldo = Math.abs(calcNetto - saldo) < 0.005;

  function delta(a: number, b: number | undefined): string | null {
    if (!compare || b == null) return null;
    return sEur(a - b);
  }

  return (
    <div>
      <ImportBanner />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
        <KpiCard
          label="Einsatz"
          value={eur(k.einsatz)}
          tone="einsatz"
          big
          info="Summe aller Einsätze im gewählten Zeitraum."
          delta={delta(k.einsatz, pk?.einsatz)}
        />
        <KpiCard
          label="Auszahlung"
          value={eur(k.gewinn)}
          tone="auszahlung"
          big
          info="Summe aller Auszahlungen im gewählten Zeitraum."
          delta={delta(k.gewinn, pk?.gewinn)}
        />
        <KpiCard
          label="Nettoergebnis"
          value={sEur(k.netto)}
          tone={k.netto >= 0 ? 'pos' : 'neg'}
          big
          info="Auszahlung minus Einsatz."
          delta={delta(k.netto, pk?.netto)}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginTop: 16 }}>
        <KpiCard
          label="Ø Einsatz pro Monat"
          value={perMonth == null ? '–' : eur(perMonth)}
          tone="einsatz"
          sub={
            nettoPerMonth == null ? undefined : (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: nettoPerMonth >= 0 ? CO.pos : CO.neg }}>
                  netto {sEur(nettoPerMonth)} / Monat
                </div>
                <div>über {span.toLocaleString('de-DE', { maximumFractionDigits: 1 })} {span === 1 ? 'Monat' : 'Monate'}</div>
              </>
            )
          }
          info="Einsatz im gewählten Zeitraum geteilt durch die Anzahl der abgedeckten Monate (bis heute). Monate ohne Spiel zählen mit 0 € mit. Die Netto-Zeile zeigt das Nettoergebnis (Auszahlung minus Einsatz) pro Monat, also was dich Lotto effektiv kostet."
          delta={perMonth == null ? null : delta(perMonth, prevPerMonth)}
        />
        <KpiCard
          label="Rückflussquote"
          value={pct(k.rueckfluss)}
          info="Anteil des Einsatzes, der als Auszahlung zurückgeflossen ist (Auszahlung ÷ Einsatz)."
          delta={compare && pk ? sPct2((k.rueckfluss || 0) - (pk.rueckfluss || 0)) : null}
        />
        <KpiCard
          label="Auszahlungsquote"
          value={pct(k.auszahlungsquote)}
          sub={`${k.payCount} von ${k.n} Einträgen`}
          info="Anteil der Einträge, bei denen überhaupt eine Auszahlung erfolgt ist (Auszahlung > 0)."
        />
        <KpiCard
          label="Nettogewinnquote"
          value={pct(k.nettogewinnquote)}
          sub={`${k.netPos} von ${k.n} Einträgen`}
          info="Anteil der Einträge mit positivem Nettoergebnis (Auszahlung > Einsatz)."
        />
      </div>

      <div style={{ marginTop: 20 }}>
        <Card>
          <CardTitle
            title="Einsatz, Auszahlung & Nettoergebnis nach Monat"
            sub={`${p.label} · Werte in Euro`}
            right={
              <Legend
                items={[
                  { l: 'Einsatz', c: CO.einsatz },
                  { l: 'Auszahlung', c: CO.auszahlung },
                  { l: 'Nettoergebnis (Linie)', c: '#3a3f37', line: true },
                  ...(compare && prevMonths ? [{ l: 'Netto Vorzeitraum', c: '#b9bfb2', dash: true }] : []),
                ]}
              />
            }
          />
          {months.length ? (
            <div style={{ overflowX: 'auto' }}>
              <FinanceChart
                rows={months}
                prevRows={compare && prevMonths && prevMonths.length === months.length ? prevMonths : null}
                colorEinsatz={CO.einsatz}
                colorAuszahlung={CO.auszahlung}
              />
            </div>
          ) : (
            <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: CO.faint }}>
              Keine Daten im gewählten Zeitraum.
            </div>
          )}
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 20, marginTop: 20 }}>
        <Card>
          <CardTitle title="Verteilung des Einsatzes nach Spielart" sub={p.label} />
          <HBarChart
            rows={arts.map((a) => ({ label: a.spielart, v: a.einsatz }))}
            colorFn={(r) => artColor(r.label)}
            valFmt={(v) => eur(v)}
          />
        </Card>
        <Card>
          <CardTitle title="Größte Auszahlungen" sub={`Einzelne Einträge · ${p.label}`} />
          {top.length ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <tbody>
                {top.map((e, i) => (
                  <tr
                    key={e.id}
                    onClick={() => setDetailId(e.id)}
                    style={{ borderTop: i ? `1px solid ${CO.border2}` : 'none', cursor: 'pointer' }}
                  >
                    <td style={{ padding: '9px 0', color: CO.muted, width: 82 }} className="num">{dstr(e.iso)}</td>
                    <td style={{ padding: '9px 6px' }}>{e.spielart}</td>
                    <td style={{ padding: '9px 0', textAlign: 'right' }} className="num">
                      <span style={{ color: CO.auszahlung, fontWeight: 600 }}>{eur(e.gewinn)}</span>
                    </td>
                    <td style={{ padding: '9px 0 9px 8px', textAlign: 'right' }}>
                      <NetCell n={e.gewinn - e.einsatz} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: '40px 0', textAlign: 'center', color: CO.faint }}>Keine Auszahlungen im Zeitraum.</div>
          )}
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 20, marginTop: 20 }}>
        <Card>
          <CardTitle title="Spielartenvergleich" sub={`${p.label} · neutrale Gegenüberstellung`} />
          <ArtTable arts={arts} compact />
        </Card>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Card>
            <CardTitle title="Aktuellste Buchungen" sub={p.label} />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {recent.map((e, i) => (
                <button
                  key={e.id}
                  onClick={() => setDetailId(e.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '9px 0',
                    border: 'none',
                    borderTop: i ? `1px solid ${CO.border2}` : 'none',
                    background: 'transparent',
                    textAlign: 'left',
                    width: '100%',
                    cursor: 'pointer',
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 2,
                      background: artColor(e.spielart),
                      flex: '0 0 8px',
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        color: CO.text,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {e.spielart}
                    </div>
                    <div className="num" style={{ fontSize: 11.5, color: CO.faint }}>
                      {dstr(e.iso)} · {tstr(e.iso)}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div className="num" style={{ fontSize: 12.5, color: CO.muted }}>
                      Einsatz {eur(e.einsatz)}
                    </div>
                    <div className="num" style={{ fontSize: 12.5 }}>
                      <NetCell n={e.gewinn - e.einsatz} />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </Card>
          {summary ? (
            <DataQualityCard
              okAll={okAll}
              calcEinsatz={allK.einsatz}
              calcGewinn={allK.gewinn}
              calcSaldo={calcNetto}
              sumEinsatz={summary.einsatzGesamt}
              sumGewinn={summary.gewinnGesamt}
              sumSaldo={saldo}
              okE={okE}
              okG={okG}
              okSaldo={okSaldo}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ImportBanner() {
  const { lastImport, history } = useApp();
  if (!lastImport || !history) return null;
  const es = history.eintraege;
  if (!es.length) return null;
  let min = es[0].iso;
  let max = es[0].iso;
  for (const e of es) {
    if (e.iso < min) min = e.iso;
    if (e.iso > max) max = e.iso;
  }
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        background: '#f3f8f3',
        border: '1px solid #cde3d0',
        borderRadius: 12,
        padding: '13px 18px',
        marginBottom: 18,
        animation: 'wlPop .2s ease',
      }}
    >
      <Icon name="check" color={CO.pos} size={20} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#2b6b3d' }}>Import erfolgreich abgeschlossen</div>
        <div className="num" style={{ fontSize: 12.5, color: CO.muted }}>
          {fmtNum(es.length)} Einträge importiert · Zeitraum {dstr(min)} – {dstr(max)}
        </div>
      </div>
    </div>
  );
}

function DataQualityCard({
  okAll,
  calcEinsatz,
  calcGewinn,
  calcSaldo,
  sumEinsatz,
  sumGewinn,
  sumSaldo,
  okE,
  okG,
  okSaldo,
}: {
  okAll: boolean;
  calcEinsatz: number;
  calcGewinn: number;
  calcSaldo: number;
  sumEinsatz: number;
  sumGewinn: number;
  sumSaldo: number;
  okE: boolean;
  okG: boolean;
  okSaldo: boolean;
}) {
  return (
    <div
      style={{
        background: okAll ? '#f3f8f3' : '#fdf6ec',
        border: `1px solid ${okAll ? '#d3e6d5' : '#eddcc0'}`,
        borderRadius: 12,
        padding: 18,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 10 }}>
        <Icon name={okAll ? 'check' : 'warn'} color={okAll ? CO.pos : '#b8801f'} size={18} />
        <div style={{ fontSize: 14, fontWeight: 600, color: okAll ? '#2b6b3d' : '#8a5e12' }}>
          {okAll ? 'Kontrollsumme stimmt überein' : 'Abweichung der Kontrollsumme'}
        </div>
      </div>
      <div style={{ fontSize: 12.5, color: CO.muted, marginBottom: 12, lineHeight: 1.5 }}>
        {okAll
          ? 'Die aus den Einzelbuchungen neu berechneten Summen stimmen mit der im Import enthaltenen Zusammenfassung überein.'
          : 'Die neu berechneten Summen weichen von der importierten Zusammenfassung ab. Die Anzeige verwendet die aus den Einträgen berechneten Werte.'}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
        <thead>
          <tr style={{ color: CO.faint, textAlign: 'right' }}>
            <th style={{ textAlign: 'left', fontWeight: 500, paddingBottom: 6 }}></th>
            <th style={{ fontWeight: 500, paddingBottom: 6 }}>Berechnet</th>
            <th style={{ fontWeight: 500, paddingBottom: 6 }}>Zusammenfassung</th>
          </tr>
        </thead>
        <tbody>
          <DqRow label="Einsatz" a={calcEinsatz} b={sumEinsatz} ok={okE} signed={false} />
          <DqRow label="Auszahlung" a={calcGewinn} b={sumGewinn} ok={okG} signed={false} />
          <DqRow label="Nettoergebnis" a={calcSaldo} b={sumSaldo} ok={okSaldo} signed={true} />
        </tbody>
      </table>
    </div>
  );
}

function DqRow({ label, a, b, ok, signed }: { label: string; a: number; b: number; ok: boolean; signed: boolean }) {
  return (
    <tr style={{ borderTop: '1px solid rgba(0,0,0,.06)' }}>
      <td style={{ padding: '7px 0', color: CO.text }}>{label}</td>
      <td className="num" style={{ padding: '7px 0', textAlign: 'right', color: CO.text, fontWeight: 500 }}>
        {signed ? sEur(a) : eur(a)}
      </td>
      <td
        className="num"
        style={{
          padding: '7px 0',
          textAlign: 'right',
          color: ok ? CO.muted : '#b8801f',
          fontWeight: ok ? 400 : 600,
        }}
      >
        {(signed ? sEur(b || 0) : eur(b || 0)) + (ok ? '' : ' ✳')}
      </td>
    </tr>
  );
}

function ArtTable({ arts, compact }: { arts: ReturnType<typeof artRows>; compact: boolean }) {
  if (!arts.length) return <div style={{ padding: 40, textAlign: 'center', color: CO.faint }}>Keine Daten.</div>;
  const cols = compact
    ? ['Spielart', 'n', 'Einsatz', 'Auszahlung', 'Netto', 'Rückfluss']
    : ['Spielart', 'Einträge', 'Einsatz', 'Auszahlung', 'Nettoergebnis', 'Rückflussquote', 'Auszahlungsquote', 'Nettogewinnquote', 'Ø Einsatz', 'Höchste Ausz.'];
  const aligns = compact
    ? ['l', 'r', 'r', 'r', 'r', 'r']
    : ['l', 'r', 'r', 'r', 'r', 'r', 'r', 'r', 'r', 'r'];
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
      <thead>
        <tr style={{ color: CO.faint }}>
          {cols.map((c, i) => (
            <th
              key={i}
              style={{
                textAlign: aligns[i] === 'r' ? 'right' : 'left',
                fontWeight: 500,
                padding: '0 0 8px',
                whiteSpace: 'nowrap',
              }}
            >
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {arts.map((a) => (
          <tr key={a.spielart} style={{ borderTop: `1px solid ${CO.border2}` }}>
            <td style={{ padding: '9px 8px 9px 0' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 9, height: 9, borderRadius: 2, background: artColor(a.spielart) }} />
                {a.spielart}
              </span>
            </td>
            <td className="num" style={{ textAlign: 'right', padding: '9px 8px', color: CO.muted }}>
              {fmtNum(a.n)}
            </td>
            <td style={{ textAlign: 'right', padding: '9px 8px' }}>
              <EurCell value={a.einsatz} />
            </td>
            <td style={{ textAlign: 'right', padding: '9px 8px' }}>
              <EurCell value={a.gewinn} tone="auszahlung" />
            </td>
            <td style={{ textAlign: 'right', padding: '9px 8px' }}>
              <NetCell n={a.netto} />
            </td>
            <td className="num" style={{ textAlign: 'right', padding: '9px 8px', color: CO.muted }}>
              {pct(a.rueckfluss)}
            </td>
            {!compact ? (
              <>
                <td className="num" style={{ textAlign: 'right', padding: '9px 8px', color: CO.muted }}>
                  {pct(a.auszahlungsquote)}
                </td>
                <td className="num" style={{ textAlign: 'right', padding: '9px 8px', color: CO.muted }}>
                  {pct(a.nettogewinnquote)}
                </td>
                <td style={{ textAlign: 'right', padding: '9px 8px' }}>
                  <EurCell value={a.avgEinsatz} />
                </td>
                <td style={{ textAlign: 'right', padding: '9px 8px' }}>
                  <EurCell value={a.maxG} />
                </td>
              </>
            ) : null}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function sPct2(n: number): string {
  const sign = n > 0 ? '+' : n < 0 ? '−' : '';
  return sign + Math.abs(n).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' %';
}
